import React, { useState, useRef } from 'react'
import { PhoneNumberInputProps } from './types'
import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { CountrySelectionModal } from './CountrySelectionModal'
import { CountryDTO } from '@/domain/countries/countriesTypes'
import { useGetCountriesCatalog } from '@/domain/countries/useCases'

// Default country (Kuwait)
const DEFAULT_COUNTRY: CountryDTO = {
  id: 118,
  code: 965,
  short_name: 'KW',
  img_url: 'https://flagcdn.com/w40/kw.png',
  name: 'Kuwait',
}

// Простая маска для номера телефона
const formatPhoneNumber = (value: string, _countryCode: string): string => {
  // Убираем все нецифровые символы
  const digits = value.replace(/\D/g, '')

  // Простое форматирование (можно улучшить)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
  if (digits.length <= 10)
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  withoutBorder = false,
  onChange,
  value,
  errorMessage,
  afterBlur,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [showCountryModal, setShowCountryModal] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const theme = useTheme()
  const { data: countriesData } = useGetCountriesCatalog()

  // Find the current country based on the value prop, fallback to default
  const getCurrentCountry = (): CountryDTO => {
    if (countriesData?.countries && value?.countryIsoCode) {
      const foundCountry = countriesData.countries.find(
        (c) => c.short_name === value.countryIsoCode,
      )
      if (foundCountry) {
        return foundCountry
      }
    }
    return DEFAULT_COUNTRY
  }

  const [selectedCountry, setSelectedCountry] = useState<CountryDTO>(() =>
    getCurrentCountry(),
  )

  // Update selected country when countries data becomes available
  React.useEffect(() => {
    if (
      countriesData?.countries &&
      selectedCountry === DEFAULT_COUNTRY &&
      value?.countryIsoCode
    ) {
      const foundCountry = countriesData.countries.find(
        (c) => c.short_name === value.countryIsoCode,
      )
      if (foundCountry) {
        setSelectedCountry(foundCountry)
      }
    }
  }, [countriesData?.countries]) // Only update when countries data loads

  const handleFocus = () => setIsFocused(true)

  const handleBlur = () => {
    setIsFocused(false)
    afterBlur?.()
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const formattedNumber = formatPhoneNumber(
      inputValue,
      String(selectedCountry.code),
    )

    onChange({
      number: formattedNumber,
      countryCode: `+${selectedCountry.code}`,
      countryIsoCode: selectedCountry.short_name,
    })
  }

  const handleCountrySelect = (country: CountryDTO) => {
    setSelectedCountry(country)

    onChange({
      number: '',
      countryCode: `+${country.code}`,
      countryIsoCode: country.short_name,
    })

    // Фокусируем инпут после выбора страны
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const inputHeight = '70px'

  // Получаем placeholder для выбранной страны
  const getPlaceholderForCountry = (country: CountryDTO): string => {
    const placeholders: Record<string, string> = {
      KW: '123 4567',
      US: '123 456 7890',
      RU: '123 456 78 90',
      GB: '7700 123456',
      DE: '123 4567890',
      FR: '1 23 45 67 89',
      IT: '123 456 7890',
      ES: '123 45 67 89',
      NL: '6 12345678',
      CH: '78 123 45 67',
    }
    return placeholders[country.short_name] || '123 456 789'
  }

  return (
    <>
      {/* Основной контейнер */}
      <BaseView row align="center" gap={8} style={{ position: 'relative' }}>
        {/* Кнопка выбора страны */}
        <button
          type="button"
          onClick={() => setShowCountryModal(true)}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingTop: '16px',
            paddingBottom: '16px',
            height: inputHeight,
            backgroundColor: theme.colors.background.textInput,
            borderRadius: '16px',
            border: withoutBorder
              ? 'none'
              : `1.5px solid ${theme.colors.border.inputTyping}`,
            cursor: 'pointer',
            minWidth: '120px',
          }}
        >
          <img
            src={selectedCountry.img_url}
            alt={selectedCountry.name}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '12px',
              objectFit: 'cover',
            }}
          />
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.textTextInput"
            style={{
              lineHeight: '20px',
              fontWeight: '400',
              letterSpacing: '0',
            }}
          >
            +{selectedCountry.code}
          </BaseText>
        </button>

        {/* Инпут для номера телефона */}
        <div style={{ flexGrow: 1, position: 'relative' }}>
          <input
            ref={inputRef}
            type="tel"
            value={value?.number || ''}
            onChange={handlePhoneChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={
              isFocused ? '' : getPlaceholderForCountry(selectedCountry)
            }
            style={{
              width: '100%',
              height: inputHeight,
              paddingLeft: '12px',
              paddingRight: '12px',
              backgroundColor: theme.colors.background.textInput,
              borderRadius: '16px',
              border: withoutBorder
                ? 'none'
                : errorMessage
                  ? `1.5px solid ${theme.colors.border.inputError}`
                  : isFocused
                    ? `1.5px solid ${theme.colors.border.inputTyping}`
                    : 'none',
              outline: 'none',
              color: theme.colors.label.textTextInput,
            }}
            className="phone-number-input ym-hide-content"
          />

          {/* Сообщение об ошибке */}
          {errorMessage && (
            <BaseText
              fontSize={12}
              color="label.inputErrorMessage"
              style={{
                position: 'absolute',
                left: '6px',
                bottom: '-20px',
              }}
            >
              {errorMessage}
            </BaseText>
          )}
        </div>
      </BaseView>

      {/* Модальное окно выбора страны */}
      <CountrySelectionModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onCountrySelect={handleCountrySelect}
        selectedCountry={selectedCountry}
      />
    </>
  )
}
